/**
 * CcqTile — CCQ (industrie de la construction) : configuration en tables de règles datées, chantiers, rapport mensuel + remise client.
 * Batch #14.250 · refonte #14.298 · chantiers / taux officiels / rapport client #14.299.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle } from "lucide-react";
import { API, authH, err } from "./_shared";
import CcqConfigTab from "./ccq/CcqConfigTab";
import CcqReportTab from "./ccq/CcqReportTab";
import SitesTab from "./ccq/SitesTab";

export default function CcqTile({ masterId, settings }) {
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(["config", "sites", "report"].includes(sp.get("tab")) ? sp.get("tab") : "config");
  const focusRuleId = sp.get("rule") || "";
  const clearFocus = useCallback(() => { if (sp.has("rule")) { const n = new URLSearchParams(sp); n.delete("rule"); n.delete("tab"); setSp(n, { replace: true }); } }, [sp, setSp]);
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [sites, setSites] = useState([]);
  const load = useCallback(async () => {
    try {
      const [c, m, s] = await Promise.all([axios.get(`${API}/admin/payroll/${masterId}/ccq/config`, { headers: authH() }), axios.get(`${API}/admin/payroll/ccq/meta`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/${masterId}/ccq/sites`, { headers: authH() })]);
      setCfg(c.data); setMeta(m.data); setSites(s.data.items || []);
    } catch (e) { err(e); }
  }, [masterId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="paie-ccq">
      {!settings.ccq_enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-center gap-2" data-testid="paie-ccq-disabled">
          <AlertTriangle className="w-4 h-4" /> Le mode CCQ n'est pas activé pour cet employeur — <Link to={`/admin/paie/${masterId}/reglages`} className="underline font-semibold">activer dans les Réglages</Link>. Vous pouvez déjà préparer la configuration ci-dessous.
        </div>
      )}
      <div className="inline-flex rounded-lg border border-slate-300 bg-white overflow-hidden text-xs">
        {[["config", "Configuration (règles & taux)"], ["sites", `Chantiers (${sites.length})`], ["report", "Rapport mensuel & remise client"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 font-semibold transition ${tab === k ? "bg-amber-600 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`paie-ccq-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "config" && cfg && meta && <CcqConfigTab masterId={masterId} cfg={cfg} setCfg={setCfg} meta={meta} focusRuleId={focusRuleId} onFocusDone={clearFocus} />}
      {tab === "sites" && cfg && meta && <SitesTab masterId={masterId} sites={sites} setSites={setSites} meta={meta} cfg={cfg} />}
      {tab === "report" && <CcqReportTab masterId={masterId} />}
    </div>
  );
}
